/**
 * SEOFormSection - SEO settings for events
 * Extracted from EventsTab.jsx
 */
import { Plus, Search, X } from "lucide-react";
import { Button } from "../../ui/button";

export const SEOFormSection = ({ 
  formData, 
  setFormData, 
  newKeyword, 
  setNewKeyword 
}) => {
  const addKeyword = () => {
    if (!newKeyword.trim()) return;
    const keywords = formData.seo?.keywords || [];
    if (!keywords.includes(newKeyword.trim())) {
      setFormData({
        ...formData,
        seo: {
          ...formData.seo,
          keywords: [...keywords, newKeyword.trim()]
        }
      });
    }
    setNewKeyword("");
  };

  const removeKeyword = (keyword) => {
    setFormData({
      ...formData,
      seo: {
        ...formData.seo,
        keywords: (formData.seo?.keywords || []).filter(k => k !== keyword)
      }
    });
  };

  return (
    <div className="border-t border-white/10 pt-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Search className="w-5 h-5 text-blue-500" />
          <div>
            <h4 className="font-secondary text-sm font-bold uppercase text-white">SEO Settings</h4>
            <p className="text-[10px] text-zinc-500">Custom search engine optimization for this event</p>
          </div>
        </div>
      </div>

      <div className="space-y-4 p-4 bg-blue-500/5 border border-blue-500/20">
        {/* Full URL Path */}
        <div>
          <label className="block text-[10px] text-zinc-500 uppercase mb-1">URL Path</label>
          <div className="flex items-center gap-1">
            <span className="text-zinc-500 text-sm">/</span>
            <input 
              type="text"
              value={formData.custom_url_path || formData.slug || ""}
              onChange={(e) => {
                let path = e.target.value.toLowerCase().replace(/[^a-z0-9-/]/g, '-').replace(/-+/g, '-').replace(/\/+/g, '/');
                if (path.startsWith('/')) path = path.slice(1);
                setFormData({...formData, custom_url_path: path, slug: path.split('/').pop() || path});
              }}
              placeholder="my-tournament or events/my-tournament"
              className="flex-1 bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm font-mono"
              data-testid="seo-url-path"
            />
          </div>
          <p className="text-[10px] text-zinc-600 mt-1">
            Full URL path for this event. Examples: <code className="text-zinc-400">holiday-classic</code>, <code className="text-zinc-400">tournaments/2026/march-madness</code>
          </p>
          <p className="text-[10px] text-amber-500/80 mt-1">
            ⚠️ Changing this will break any existing links to this event
          </p>
        </div>

        <div>
          <label className="block text-[10px] text-zinc-500 uppercase mb-1">Meta Title</label>
          <input 
            type="text"
            value={formData.seo?.meta_title || ""}
            onChange={(e) => setFormData({...formData, seo: {...formData.seo, meta_title: e.target.value}})}
            placeholder={formData.name || "Event title for search engines"}
            className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm"
            data-testid="seo-meta-title"
          />
          <p className="text-[10px] text-zinc-600 mt-1">Leave blank to use event name. Recommended: 50-60 characters</p>
        </div>

        <div>
          <label className="block text-[10px] text-zinc-500 uppercase mb-1">Meta Description</label>
          <textarea 
            value={formData.seo?.meta_description || ""}
            onChange={(e) => setFormData({...formData, seo: {...formData.seo, meta_description: e.target.value}})}
            placeholder={formData.description || "Event description for search engines"}
            rows={2}
            className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm"
            data-testid="seo-meta-description"
          />
          <p className="text-[10px] text-zinc-600 mt-1">Recommended: 150-160 characters</p>
        </div>

        <div>
          <label className="block text-[10px] text-zinc-500 uppercase mb-1">OG Image URL</label>
          <input 
            type="text"
            value={formData.seo?.og_image_url || ""}
            onChange={(e) => setFormData({...formData, seo: {...formData.seo, og_image_url: e.target.value}})}
            placeholder="https://... (image shown when shared on social media)"
            className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm"
            data-testid="seo-og-image"
          />
        </div>

        <div>
          <label className="block text-[10px] text-zinc-500 uppercase mb-1">Keywords</label>
          <div className="flex gap-2 mb-2">
            <input 
              type="text"
              value={newKeyword}
              onChange={(e) => setNewKeyword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyword())}
              placeholder="Add keyword"
              className="flex-1 bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm"
            />
            <Button
              type="button"
              onClick={addKeyword}
              disabled={!newKeyword.trim()}
              className="bg-blue-600 text-white hover:bg-blue-500 text-xs px-3 h-auto rounded-none"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          {formData.seo?.keywords?.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {formData.seo.keywords.map((keyword) => (
                <span 
                  key={keyword}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-zinc-800 text-xs text-zinc-300"
                >
                  {keyword}
                  <button
                    type="button"
                    onClick={() => removeKeyword(keyword)}
                    className="text-zinc-500 hover:text-red-500"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SEOFormSection;
